import { useEffect, useState, useCallback } from "react"; // React core
import { useParams, useNavigate } from "react-router-dom"; // React Router for navigation
import { Tab, Tabs } from "@material-ui/core"; // Material-UI components for tabs
import { CorrectionHeader } from "./components/Header"; // Header component for the correction view
import TabPanel from "../../../component/base/TabPanel"; // Custom tab panel component
import { Grid } from "../../../component/list/grid"; // Grid component for displaying data
import { Loading } from "../../../component/common/Loading"; // Loading indicator component
import { useCorrections } from "../../../hooks/corrections/useCorrections"; // Hook for fetching correction data
import { useStudentResponseObjectives } from "../../../hooks/studentResponseObjectives/useStudentResponseObjectives"; // Hook for fetching student responses for objectives
import { useStudentResponseDissertatives } from "../../../hooks/studentResponseDissertatives/useStudentResponseDissertatives"; // Hook for fetching student responses for dissertatives
import { useReviewers } from "../../../hooks/applications/Reviewers/useReviewers"; // Hook for fetching reviewers
import { GridField } from "../../../component/list/grid"; // type import

/**
 * @Component CorrectionManagerView
 * @Description Manages the correction view, handling tabs for students, reviewers, and results.
 * It fetches and displays data related to a specific correction.
 */
export function CorrectionManagerView() {
  const navigate = useNavigate();
  const { id, applicationId, diagramId } = useParams();
  const token = localStorage.getItem("token") || "";

  const { getCompletedCorrection } = useCorrections(token);
  const {
    getResponsesByAnswerSheet,
    getStudentResults: getObjectiveStudentResults,
    search: searchObjectives,
    setSearch: setSearchObjectives
  } = useStudentResponseObjectives(token);

  const {
    getResponsesByApplication,
    getStudentResults: getDissertativeStudentResults,
    search: searchDissertatives,
    setSearch: setSearchDissertatives
  } = useStudentResponseDissertatives(token);

  const [completedCorrection, setCompletedCorrection] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [correctionType, setCorrectionType] = useState<string>("objective"); 

  //! Tabs: 0 = Students, 1 = Reviewers, 2 = Results
  const [activeTab, setActiveTab] = useState<number>(0);

  //! States for "Students" tab
  const [studentResponses, setStudentResponses] = useState<any[]>([]);
  const [loadingStudentResponses, setLoadingStudentResponses] = useState(true);
  const [searchInputValue, setSearchInputValue] = useState<string>("");

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  //! States for "Results" tab
  const [studentResults, setStudentResults] = useState<any[]>([]);
  const [loadingStudentResults, setLoadingStudentResults] = useState(true);
  const [resultsPagination, setResultsPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  //! States for "Reviewers" tab (used via hook)
  const {
    reviewers,
    loading: loadingReviewers,
    setSearch: setReviewerSearch,
    pagination: reviewerPagination,
    changePage: changeReviewerPage,
  } = useReviewers(applicationId || "", token);
  const [reviewerSearchInput, setReviewerSearchInput] = useState<string>("");

  //! Debounce for search
  const debouncedSearch = useCallback((value: string) => {
    if (value.trim().length >= 3) {
      // Escolhe o setSearch correto com base no tipo de correção
      if (correctionType === "dissertative") {
        setSearchDissertatives(value);
      } else {
        setSearchObjectives(value);
      }
    } else {
      // Reseta a pesquisa para ambos os tipos
      setSearchDissertatives("");
      setSearchObjectives("");
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
      setResultsPagination((prev) => ({ ...prev, currentPage: 1 }));
    }
  }, [setSearchObjectives, setSearchDissertatives, correctionType, setPagination, setResultsPagination]);

  const handleSearchInputChange = useCallback(
    (value: string) => {
      setSearchInputValue(value);
      // Utilizamos uma versão do debounce inline para evitar problemas com dependências
      const timeoutId = setTimeout(() => {
        debouncedSearch(value);
      }, 300);
      return () => clearTimeout(timeoutId);
    },
    [debouncedSearch]
  );

  //! Debounce for reviewers
  const debouncedReviewerSearch = useCallback((value: string) => {
    setReviewerSearch(value);
  }, [setReviewerSearch]);

  const handleReviewerSearchInputChange = useCallback(
    (value: string) => {
      setReviewerSearchInput(value);
      // Utilizamos uma versão do debounce inline para evitar problemas com dependências
      const timeoutId = setTimeout(() => {
        debouncedReviewerSearch(value);
      }, 300);
      return () => clearTimeout(timeoutId);
    },
    [debouncedReviewerSearch]
  );

  //! Fetch Correction
  useEffect(() => {
    const fetchCorrection = async () => {
      if (id) {
        try {
          const data = await getCompletedCorrection(id);
          setCompletedCorrection(data);
          
          // Tratamos o data como any para acessar a propriedade correctionType
          const correctionData = data as any;
          
          // Verificar se é dissertativa com base no dado da API
          if (correctionData && correctionData.correctionType) {
            setCorrectionType(String(correctionData.correctionType));
          } else {
            // Verificar através do campo diagram, que só existe em correções dissertativas
            setCorrectionType(correctionData?.diagram ? 'dissertative' : 'objective');
          }
        } catch (error) {
          console.error("Error fetching completed correction:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchCorrection();
  }, [id, getCompletedCorrection]);


  //! Fetch Student Responses ("Students")
  useEffect(() => {
    const fetchStudentResponses = async () => {
      if (id && completedCorrection && applicationId) {
        try {
          setLoadingStudentResponses(true);
          let response;

          if (correctionType === "dissertative") {
            // Agora usamos applicationId em vez do diagramId
            response = await getResponsesByApplication(
              applicationId,
              pagination.currentPage,
              pagination.itemsPerPage,
              searchDissertatives
            );
          } else {

            console.log("completedCorrection", completedCorrection);
            // Para objetivas, continuamos usando o answerSheet.id
            const answerSheetId = completedCorrection.answerSheet?.id;
            console.log("AnswerSheet ID:", answerSheetId);
            if (!answerSheetId) {
              throw new Error("ID do gabarito não encontrado");
            }
            
            response = await getResponsesByAnswerSheet(
              answerSheetId,
              pagination.currentPage,
              pagination.itemsPerPage,
              searchObjectives
            );
          }
          

          setStudentResponses(response.students);
          if (response.pagination) {
            setPagination(response.pagination);
          }
        } catch (error) {
          console.error("Error fetching student responses:", error);
        } finally {
          setLoadingStudentResponses(false);
        }
      }
    };
    fetchStudentResponses();
  }, [
    id,
    completedCorrection,
    correctionType,
    getResponsesByAnswerSheet,
    getResponsesByApplication,
    applicationId,
    pagination.currentPage,
    pagination.itemsPerPage,
    searchObjectives,
    searchDissertatives,
  ]);

  //! Fetch Student Results ("Results")
  useEffect(() => {
    const fetchStudentResults = async () => {
      if (id && completedCorrection && applicationId) {
        try {
          setLoadingStudentResults(true);
          let response;

          if (correctionType === "dissertative") {
            // Agora usamos applicationId em vez do diagramId
            response = await getDissertativeStudentResults(
              applicationId,
              resultsPagination.currentPage,
              resultsPagination.itemsPerPage,
              searchDissertatives
            );
          } else {
            // Para objetivas, continuamos usando o answerSheet.id
            const answerSheetId = completedCorrection.answerSheet?.id;
            if (!answerSheetId) {
              throw new Error("ID do gabarito não encontrado");
            }
            
            response = await getObjectiveStudentResults(
              answerSheetId,
              resultsPagination.currentPage,
              resultsPagination.itemsPerPage,
              searchObjectives
            );
          }
          
          setStudentResults(response.results);
          if (response.pagination) {
            setResultsPagination(response.pagination);
          }
        } catch (error) {
          console.error("Error fetching student results:", error);
        } finally {
          setLoadingStudentResults(false);
        }
      }
    };
    fetchStudentResults();
  }, [
    id,
    completedCorrection,
    correctionType,
    getObjectiveStudentResults,
    getDissertativeStudentResults,
    applicationId,
    resultsPagination.currentPage,
    resultsPagination.itemsPerPage,
    searchObjectives,
    searchDissertatives,
  ]);

  //! Handlers for pagination
  const handleChangePage = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  const handleChangeResultsPage = useCallback((newPage: number) => {
    setResultsPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  //! Tab change handler
  const handleChangeTab = useCallback(
    (_: any, newValue: number) => {
      setActiveTab(newValue);
      navigate({ search: `?tab=${newValue}` });
    },
    [navigate]
  );

  //! Mapping data for the Grid
  const gridItems = studentResponses.map((x) => ({
    _id: x.id,
    name: correctionType === "dissertative" ? x.name ?? "" : x.person?.name ?? "",
    email: correctionType === "dissertative" ? x.email ?? "" : x.person?.email ?? "",
    organization: x.organization?.name ?? "Não informado",
    createdAt: x.createdAt
      ? new Date(x.createdAt).toLocaleDateString()
      : "Não informado",
  }));

  const resultsGridItems = studentResults.map((result) => ({
    _id: result.student.id,
    Student: result.student.name,
    ...(correctionType !== "dissertative" ? { notMarked: result.notMarked } : {}),
    incorrect: result.incorrect,
    correct: result.correct,
    ...(correctionType === "dissertative" ? {
      undefinedByReviewer: result.undefinedByReviewer || 0,
      percentCorrect: `${result.percentCorrect || 0}%`,
    } : {}),
    total: result.total,
  }));

  //! Field configuration for the "Results" tab
  const fieldsResults: GridField[] = [
    {
      name: "Student",
      type: "text",
      header: "Aluno",
      customClass: "custom-aluno",
    },
    ...(correctionType !== "dissertative" ? [
      {
        name: "notMarked",
        type: "text",
        header: "Não Marcadas",
        customClass: "custom-not-marked",
      },
    ] as GridField[] : []),
    {
      name: "incorrect",
      type: "text",
      header: "Incorretas",
      customClass: "custom-incorrect",
    },
    {
      name: "correct",
      type: "text",
      header: "Corretas",
      customClass: "custom-correct",
    },
    ...(correctionType === "dissertative" ? [
      {
        name: "undefinedByReviewer",
        type: "text" as "text",
        header: "Não Corrigidas",
        customClass: "custom-undefined",
      },
      {
        name: "percentCorrect",
        type: "text" as "text",
        header: "Percentual de Acertos",
        customClass: "custom-percent-correct",
      }
    ] as GridField[] : []),
    {
      name: "total",
      type: "text",
      header: "Total",
      customClass: "custom-total",
    },
  ];

  // Determina a URL para navegação dos alunos com base no tipo de correção
  const getStudentViewUrl = (studentId: string) => {
    if (correctionType === "dissertative" && applicationId) {
      return `/tests/corrections/view-dissertative/${applicationId}/${studentId}`;
    } else {
      return `/tests/corrections/view-objectives/${applicationId}/${completedCorrection?.answerSheet?.id}/${studentId}`;
    }
  };

  // Determina a URL para visualização de resultados com base no tipo de correção
  const getResultViewUrl = (studentId: string) => {
    if (correctionType === "dissertative" && applicationId) {
      return `/corrections/results-dissertative/${applicationId}/${studentId}`;
    } else {
      return `/corrections/results/${completedCorrection?.answerSheet?.id}/${studentId}`;
    }
  };

  return (
    <div className="container-fluid py-3 px-3 bg-light">
      {isLoading ? (
        <Loading />
      ) : completedCorrection ? (
        <>
          <CorrectionHeader
            completedCorrection={completedCorrection}
            aplicationId={applicationId}
            diagramId={diagramId}
          />

          <Tabs
            value={activeTab}
            onChange={handleChangeTab}
            style={{ backgroundColor: "#fafafa", marginBottom: "1rem" }}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="on"
          >
            <Tab label="Alunos" />
            <Tab label="Corretores" />
            <Tab label="Resultados" />
          </Tabs>

          {/* Tab 0: Students */}
          <TabPanel value={activeTab} index={0}>
            {loadingStudentResponses ? (
              <Loading />
            ) : (
              <Grid
                onChangeSearchInput={handleSearchInputChange}
                search_input_value={searchInputValue}
                loading_search={loadingStudentResponses}
                loading_screen={loadingStudentResponses}
                fields={[
                  { name: "name", type: "text", header: "Nome" },
                  { name: "email", type: "text", header: "E-mail" },
                  { name: "organization", type: "text", header: "Organização" },
                  {
                    name: "createdAt",
                    type: "text",
                    header: "Data de Criação",
                  },
                ]}
                items={gridItems}
                onManagerActionClick={(id: string) => {
                  navigate(getStudentViewUrl(id));
                }}
                hideEdit
                hideDelete
                hideBtn
                not_found_message="Nenhum aluno encontrado."
                button_text="Adicionar alunos"
                button_link={`/tests/students/create`}
                search_text="Pesquisar pelo nome do aluno"
                pagination={{
                  last_page: pagination.totalPages,
                  current_page: pagination.currentPage,
                }}
                onChangePagination={handleChangePage}
                top_text="Alunos vinculados à correção"
                onSearchButtonClick={() => {}}
              />
            )}
          </TabPanel>

          {/* Tab 1: Reviewers */}
          <TabPanel value={activeTab} index={1}>
            <Grid
              hideManager
              onChangeSearchInput={handleReviewerSearchInputChange}
              search_input_value={reviewerSearchInput}
              loading_search={loadingReviewers}
              loading_screen={loadingReviewers}
              fields={[
                { name: "reviewerName", type: "text", header: "Nome" },
                { name: "reviewerEmail", type: "text", header: "E-mail" },
              ]}
              items={reviewers.map((rev) => ({
                _id: rev.id,
                reviewerName: rev.user?.name ?? "",
                reviewerEmail: rev.user?.email ?? "N/A",
              }))}
              onManagerActionClick={(id: string) => {
                window.open(
                  `${window.location.origin}/tests/view/${completedCorrection?.application?.id}/${id}`
                );
              }}
              hideEdit
              hideDelete
              hideBtn
              hideActions
              not_found_message="Nenhum corretor encontrado."
              button_text="Adicionar corretores"
              button_link={`/tests/reviewers/${completedCorrection?.application?.id}/create`}
              search_text="Pesquisar pelo nome do corretor"
              top_text="Corretores vinculados à correção"
              pagination={{
                last_page: reviewerPagination.totalPages,
                current_page: reviewerPagination.currentPage,
              }}
              onChangePagination={changeReviewerPage}
              onSearchButtonClick={() => {}}
            />
          </TabPanel>

          {/* Tab 2: Results */}
          <TabPanel value={activeTab} index={2}>
            {loadingStudentResults ? (
              <Loading />
            ) : (
              <Grid
                onChangeSearchInput={handleSearchInputChange}
                search_input_value={searchInputValue}
                loading_search={loadingStudentResults}
                loading_screen={loadingStudentResults}
                fields={fieldsResults}
                items={resultsGridItems}
                onManagerActionClick={(id: string) => {
                  navigate(getResultViewUrl(id));
                }}
                hideEdit
                hideDelete
                hideBtn
                hideActions
                not_found_message="Nenhum resultado encontrado."
                button_text="Adicionar resultados"
                button_link={`/tests/results/create`}
                search_text="Pesquisar pelo nome do aluno"
                top_text="Resultados agregados"
                pagination={{
                  last_page: resultsPagination.totalPages,
                  current_page: resultsPagination.currentPage,
                }}
                onChangePagination={handleChangeResultsPage}
                onSearchButtonClick={() => {}}
              />
            )}
          </TabPanel>
        </>
      ) : (
        <div className="alert alert-info shadow-sm">
          <i className="bi bi-info-circle me-2"></i>
          Nenhuma correção encontrada.
        </div>
      )}
    </div>
  );
}
